import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Centers
  centers: [],
  centersLoading: false,
  centersError: null,
  selectedCenter: null,
  country: 'USA',

  // Camps
  camps: [],
  campsLoading: false,
  campsError: null,
  selectedCamp: null,

  // Students
  unregisteredStudents: [],
  registeredStudents: [],
  studentsLoading: false,
  studentsError: null,

  // Registration tracking
  lastRegInfo: null,   // { studentId, registrationId }
  activeTab: 'unregistered',

  // Fee calculation
  calculation: null,
  calcLoading: false,

  // Accommodation pricing from GetAccommodationFeeAndCampDate
  accommodationConfig: null,  // { accommodationFee, campStartDate, campEndDate }

  // Saved visit dates for the current pending registration
  // Persists across navigation; cleared only after confirmed payment
  savedVisitDates: [],

  // Payment info passed to separate YkVisitPayment page
  paymentInfo: null,

  // Pending edit payload — applied after payment succeeds on payment page
  pendingEditPayload: null,
};

const ykVisitSlice = createSlice({
  name: 'ykVisit',
  initialState,
  reducers: {
    // Centers
    setCenters: (state, action) => {
      state.centers = action.payload;
      state.centersError = null;
    },
    setCentersLoading: (state, action) => {
      state.centersLoading = action.payload;
    },
    setCentersError: (state, action) => {
      state.centersError = action.payload;
    },
    setSelectedCenter: (state, action) => {
      state.selectedCenter = action.payload;
    },
    setCountry: (state, action) => {
      state.country = action.payload;
    },

    // Camps
    setCamps: (state, action) => {
      state.camps = action.payload;
      state.campsError = null;
    },
    setCampsLoading: (state, action) => {
      state.campsLoading = action.payload;
    },
    setCampsError: (state, action) => {
      state.campsError = action.payload;
    },
    setSelectedCamp: (state, action) => {
      state.selectedCamp = action.payload;
    },

    // Students
    setUnregisteredStudents: (state, action) => {
      state.unregisteredStudents = action.payload;
    },
    setRegisteredStudents: (state, action) => {
      state.registeredStudents = action.payload;
    },
    setStudentsLoading: (state, action) => {
      state.studentsLoading = action.payload;
    },
    setStudentsError: (state, action) => {
      state.studentsError = action.payload;
    },

    // Registration
    setLastRegInfo: (state, action) => {
      state.lastRegInfo = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // Calculation
    setCalculation: (state, action) => {
      state.calculation = action.payload;
      state.calcLoading = false;
    },
    setCalcLoading: (state, action) => {
      state.calcLoading = action.payload;
    },

    // Accommodation config
    setAccommodationConfig: (state, action) => {
      state.accommodationConfig = action.payload;
    },

    // Saved visit dates persistence
    setSavedVisitDates: (state, action) => {
      state.savedVisitDates = action.payload;
    },

    // Payment info for separate payment page
    setPaymentInfo: (state, action) => {
      state.paymentInfo = action.payload;
    },

    // Pending edit payload
    setPendingEditPayload: (state, action) => {
      state.pendingEditPayload = action.payload;
    },

    // Reset full module state on navigation away
    clearYkVisitData: () => initialState,

    // Reset camp + student data when center changes
    // NOTE: calculation and lastRegInfo are intentionally NOT cleared here —
    // they persist across navigation and are only cleared after confirmed payment.
    clearCampData: (state) => {
      state.camps = [];
      state.campsError = null;
      state.selectedCamp = null;
      state.unregisteredStudents = [];
      state.registeredStudents = [];
      state.studentsError = null;
      state.activeTab = 'unregistered';
    },
  },
});

export const {
  setCenters,
  setCentersLoading,
  setCentersError,
  setSelectedCenter,
  setCountry,
  setCamps,
  setCampsLoading,
  setCampsError,
  setSelectedCamp,
  setUnregisteredStudents,
  setRegisteredStudents,
  setStudentsLoading,
  setStudentsError,
  setLastRegInfo,
  setActiveTab,
  setCalculation,
  setCalcLoading,
  setAccommodationConfig,
  setSavedVisitDates,
  setPaymentInfo,
  setPendingEditPayload,
  clearYkVisitData,
  clearCampData,
} = ykVisitSlice.actions;

export default ykVisitSlice.reducer;
